/**
 * Renderizador DOCX compartilhado dos 5 templates Easy Solutions.
 *
 * Consome o "document model" (array plano de blocos tipados, ver
 * `sections/documentModel.js` de cada template) e devolve um Blob .docx via
 * `docx` (pacote client-side, sem motor de renderização externo).
 *
 * Fidelidade visual buscada (pedido da Julia Buzzo / investigação de
 * viabilidade prévia):
 * - Corpo: Arial 12pt, justificado, recuo de 1ª linha 1.25cm, espaçamento
 *   0pt antes / 8pt depois, entrelinha 1.16 — mesmos valores usados nos
 *   `<p>` dos `Content.js` (ver `_shared/PageMargins.js` e os `Content.js`
 *   de cada template para a origem desses números).
 * - Título da capa: Arial 16pt, centralizado — mesmo valor explícito usado
 *   no `<h2 style={{ fontSize: '16pt' }}>` da capa de cada template.
 * - Demais títulos (h2 "Sumário", h3 numerados "1.", "2." ...): não têm
 *   `fontSize` explícito no PDF (herdam o tamanho padrão do navegador para
 *   h2/h3). Aqui usamos 14pt em negrito como aproximação razoável — não é
 *   pixel-perfect com o UA stylesheet do Chromium, mas é uma escolha única e
 *   consistente (ver limitação no PR).
 * - Margens de página: 3cm topo/esquerda, 2cm base/direita — mesmos valores
 *   de `_shared/PageMargins.js` (`@page { margin: 3cm 2cm 2cm 3cm }`).
 * - Quebra de página antes de cada título principal numerado — mesma regra
 *   da PR #779 (`pageBreakBefore` em `components/pdf/View.js`), replicada
 *   aqui via `pageBreakBefore: true` nativo do `docx` (ver bloco
 *   `{ type: 'heading', pageBreakBefore }` no document model).
 * - Sumário: NÃO tentamos replicar os "leaders" pontilhados do PDF pixel a
 *   pixel. Usamos o campo nativo `TableOfContents` do Word, que lista os
 *   títulos marcados como Heading1/Heading2. O Word só preenche o campo
 *   quando o usuário abre o arquivo e atualiza os campos (Ctrl+A, F9, ou o
 *   prompt "Update Table" que o Word mostra ao abrir) — comportamento nativo
 *   do Word, não um bug deste gerador.
 */
import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  HeadingLevel,
  ImageRun,
  PageBreak,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableOfContents,
  TableRow,
  TextRun,
  WidthType,
  convertMillimetersToTwip
} from 'docx'

const FONT = 'Arial'

// Tamanhos em "half-points" (unidade do docx para fontSize): 1pt = 2 half-points.
const BODY_SIZE = 24 // 12pt
const COVER_TITLE_SIZE = 32 // 16pt
const HEADING_SIZE = 28 // 14pt aproximado (ver nota no topo do arquivo)
const FOOTER_SIZE = 14 // 7pt, mesmo valor usado em Footer.js

// Espaçamento de parágrafo do corpo (ver Content.js: marginBottom '8pt', lineHeight 1.16).
const PARAGRAPH_SPACING = { before: 0, after: 160 /* 8pt em twips (8 * 20) */ }
const LINE_SPACING = { line: 278 /* 240 * 1.16, arredondado */, lineRule: 'auto' }
const FIRST_LINE_INDENT = convertMillimetersToTwip(12.5) // 1.25cm

// Margens de página (ver _shared/PageMargins.js: 3cm topo/esquerda, 2cm base/direita).
const PAGE_MARGIN = {
  top: convertMillimetersToTwip(30),
  right: convertMillimetersToTwip(20),
  bottom: convertMillimetersToTwip(20),
  left: convertMillimetersToTwip(30)
}

const HEADING_LEVEL_BY_MODEL_LEVEL = {
  1: HeadingLevel.HEADING_1,
  2: HeadingLevel.HEADING_2
}

const IMAGE_BOX = { maxWidth: 260, maxHeight: 190 }

const scaleToFit = (naturalWidth, naturalHeight, maxWidth, maxHeight) => {
  if (!naturalWidth || !naturalHeight) return { width: maxWidth, height: maxHeight }
  const ratio = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight, 1)
  return {
    width: Math.round(naturalWidth * ratio),
    height: Math.round(naturalHeight * ratio)
  }
}

// Só funciona no browser (precisa de <img> para medir dimensões naturais sem
// precisar de canvas/CORS). Em qualquer outro ambiente (ex.: o script de
// sanidade em Node), simplesmente não conseguimos medir e caímos no
// tamanho-padrão da caixa — o chamador sempre recebe um tamanho válido.
const measureImage = src =>
  new Promise(resolve => {
    if (typeof window === 'undefined' || typeof window.Image === 'undefined') {
      resolve(null)
      return
    }
    const img = new window.Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => resolve(null)
    img.src = src
  })

const fetchImageData = async src => {
  if (typeof fetch === 'undefined') throw new Error('fetch indisponível para buscar imagem')
  const response = await fetch(src)
  if (!response.ok) throw new Error(`Falha ao buscar imagem (${response.status}): ${src}`)
  return response.arrayBuffer()
}

/**
 * Busca uma imagem (logo, foto de checklist, etc.) e devolve um `ImageRun`
 * pronto para uso, com o tamanho ajustado à caixa informada preservando a
 * proporção original. Nunca lança: se a busca falhar (ex.: URL indisponível
 * neste ambiente), devolve `null` e quem chamou decide o que renderizar no
 * lugar (ver `buildImageGridTable`).
 */
export const buildImageRun = async (src, box = IMAGE_BOX) => {
  if (!src) return null
  try {
    const [data, dimensions] = await Promise.all([fetchImageData(src), measureImage(src)])
    const { width, height } = scaleToFit(
      dimensions?.width,
      dimensions?.height,
      box.maxWidth || IMAGE_BOX.maxWidth,
      box.maxHeight || IMAGE_BOX.maxHeight
    )
    return new ImageRun({ data, transformation: { width, height } })
  } catch (err) {
    return null
  }
}

const bodyParagraphProps = ({ align = AlignmentType.JUSTIFIED, indent = true, spacing } = {}) => ({
  alignment: align,
  indent: indent ? { firstLine: FIRST_LINE_INDENT } : undefined,
  spacing: spacing || PARAGRAPH_SPACING
})

const textRun = (text, opts = {}) => new TextRun({ text: text ?? '', font: FONT, size: BODY_SIZE, ...opts })

// Várias respostas guardam `\n` para quebras de linha manuais (ver Content.js,
// ex.: pergunta "Tensão da Central"). `docx` não interpreta `\n` dentro de um
// TextRun, então quebramos em vários TextRun com `break: 1` entre eles.
const buildRuns = (text, opts = {}) =>
  String(text ?? '')
    .split('\n')
    .map((line, index) => textRun(line, { ...opts, break: index > 0 ? 1 : 0 }))

const paragraphBlock = block =>
  new Paragraph({
    ...bodyParagraphProps({ indent: block.indent !== false }),
    children: buildRuns(block.text, { bold: block.bold, italics: block.italic })
  })

const keyValueBlock = block =>
  new Paragraph({
    ...bodyParagraphProps(),
    children: [textRun(`${block.label}: `, { bold: true }), ...buildRuns(block.value)]
  })

const headingBlock = block => {
  const isCoverTitle = block.level === 0
  return new Paragraph({
    heading: isCoverTitle ? undefined : HEADING_LEVEL_BY_MODEL_LEVEL[block.level] || HeadingLevel.HEADING_1,
    alignment: block.align || (isCoverTitle ? AlignmentType.CENTER : AlignmentType.LEFT),
    pageBreakBefore: !!block.pageBreakBefore,
    spacing: { before: 0, after: 160 },
    children: (Array.isArray(block.lines) ? block.lines : [block.text]).map(
      (line, index) =>
        new TextRun({
          text: line,
          font: FONT,
          size: isCoverTitle ? COVER_TITLE_SIZE : HEADING_SIZE,
          bold: true,
          break: index > 0 ? 1 : 0
        })
    )
  })
}

const dividerBlock = () =>
  new Paragraph({
    spacing: { before: 80, after: 80 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'CCCCCC', space: 1 } },
    children: []
  })

const listBlock = block =>
  (block.items || []).map(
    item =>
      new Paragraph({
        spacing: PARAGRAPH_SPACING,
        children: [textRun(`✓ ${item}`)]
      })
  )

const pageBreakBlock = () => new Paragraph({ children: [new PageBreak()] })

const tocBlock = block =>
  new TableOfContents(block.title || 'Sumário', {
    hyperlink: true,
    headingStyleRange: '1-2'
  })

const noBorder = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' }
const noBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder }

const buildImageCell = async image => {
  const run = await buildImageRun(image.src, IMAGE_BOX)
  const children = [
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: 40 },
      children: [run || textRun('[Imagem não disponível]', { italics: true })]
    })
  ]

  if (image.description) {
    children.push(
      new Paragraph({
        ...bodyParagraphProps({ align: AlignmentType.CENTER, indent: false, spacing: { before: 0, after: 40 } }),
        children: [textRun(image.description, { size: 20 })]
      })
    )
  }

  return new TableCell({ borders: noBorders, width: { size: 33, type: WidthType.PERCENTAGE }, children })
}

// Mesma técnica de agrupamento por 3 usada em `Content.js#renderImages` (chunkArray),
// só que como tabela (3 colunas) em vez de flexbox, já que `docx` não tem flex-wrap.
const buildImageGridTable = async images => {
  const rows = []
  for (let i = 0; i < images.length; i += 3) {
    const group = images.slice(i, i + 3)
    const cells = await Promise.all(group.map(buildImageCell))
    while (cells.length < 3) {
      cells.push(new TableCell({ borders: noBorders, width: { size: 33, type: WidthType.PERCENTAGE }, children: [] }))
    }
    rows.push(new TableRow({ children: cells }))
  }
  return new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, borders: noBorders, rows })
}

const buildBlockNodes = async block => {
  switch (block.type) {
    case 'coverPage':
      return [headingBlock({ level: 0, lines: block.lines })]
    case 'heading':
      return [headingBlock(block)]
    case 'paragraph':
      return [paragraphBlock(block)]
    case 'keyValue':
      return [keyValueBlock(block)]
    case 'imageGrid':
      return block.images?.length ? [await buildImageGridTable(block.images)] : []
    case 'pageBreak':
      return [pageBreakBlock()]
    case 'divider':
      return [dividerBlock()]
    case 'list':
      return listBlock(block)
    case 'toc':
      return [
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.LEFT,
          children: [new TextRun({ text: block.title || 'Sumário', font: FONT, size: HEADING_SIZE, bold: true })]
        }),
        tocBlock(block)
      ]
    default:
      return []
  }
}

const buildHeader = async ({ logoSrc, logoBox }) => {
  const logoRun = logoSrc ? await buildImageRun(logoSrc, logoBox || { maxWidth: 200, maxHeight: 60 }) : null

  return new Header({
    children: [
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { before: 0, after: 80 },
        border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'CCCCCC', space: 4 } },
        children: [logoRun || new TextRun('')]
      })
    ]
  })
}

const buildFooter = footerLines =>
  new Footer({
    children: (footerLines || []).map(
      line =>
        new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          spacing: { before: 0, after: 20 },
          children: [new TextRun({ text: line, font: FONT, size: FOOTER_SIZE })]
        })
    )
  })

/**
 * Gera o `Document` do `docx` a partir do document model.
 *
 * @param {{ title?: string, blocks: object[] }} model - saída de `buildDocumentModel()` de cada template.
 * @param {{ logoSrc?: string, logoBox?: object, footerLines?: string[] }} brand - header/footer/marca (mesmo conteúdo em todos os 5 templates Easy Solutions, ver `EASYSOLUTIONS_BRAND`).
 */
export const buildEasySolutionsDocxDocument = async (model, brand = {}) => {
  const blocks = model?.blocks || []
  const children = (await Promise.all(blocks.map(buildBlockNodes))).flat()

  return new Document({
    title: model?.title,
    creator: 'Btime',
    styles: {
      default: {
        document: { run: { font: FONT, size: BODY_SIZE } }
      }
    },
    sections: [
      {
        properties: { page: { margin: PAGE_MARGIN } },
        headers: { default: await buildHeader(brand) },
        footers: { default: buildFooter(brand.footerLines) },
        children
      }
    ]
  })
}

export const generateEasySolutionsDocxBlob = async (model, brand = {}) => {
  const document = await buildEasySolutionsDocxDocument(model, brand)
  return Packer.toBlob(document)
}

// Header/footer/marca compartilhados pelos 5 templates Easy Solutions — mesmo
// logo e mesmo rodapé de contato usados em todos os `Header.js`/`Footer.js` atuais.
export const EASYSOLUTIONS_BRAND = {
  logoSrc: '/pdfs/easysolutions/logo-easysolutions.png',
  logoBox: { maxWidth: 200, maxHeight: 60 },
  footerLines: [
    'Rua: Antônio Borges 172, Jardim Chapadão, Campinas - SP',
    'Telefone: 19-4107-1003 / 19-9256-7950 / 19-9695-0260 / 19-9882-9819',
    'E-mail: comercial2@easysolution.net.br / eduardo@easysolution.net.br / henrique@easysolution.net.br',
    'Site: http://easysolutionservicos.com.br'
  ]
}
